import { ArrowDown, ArrowUp, Minus } from "@phosphor-icons/react";
import { forwardRef } from "react";
import { cn } from "@/lib/cn";
import { Card } from "@/components/primitives/Card";
import { Skeleton } from "@/components/ui/feedback/Skeleton";
import type { MetricCardProps, TrendDirection } from "./MetricCard.types";

const trendClassName: Record<TrendDirection, string> = {
  up: "bfrs-bg-success-soft bfrs-text-success-contrast",
  down: "bfrs-bg-danger-soft bfrs-text-danger-contrast",
  neutral: "bfrs-bg-surface-muted bfrs-text-text-secondary"
};

const trendIcon = {
  up: <ArrowUp size={11} weight="bold" />,
  down: <ArrowDown size={11} weight="bold" />,
  neutral: <Minus size={11} weight="bold" />
};

export const MetricCard = forwardRef<HTMLDivElement, MetricCardProps>(
  ({ className, title, value, subtitle, description, trendValue, trendDirection = "neutral", icon, action, loading, error, compact, ...props }, ref) => (
    <Card ref={ref} padding={compact ? "sm" : "md"} className={cn("bfrs-min-w-0", className)} {...props}>
      <div className="bfrs-flex bfrs-items-start bfrs-justify-between bfrs-gap-3">
        <div className="bfrs-min-w-0">
          <div className="bfrs-text-[10.5px] bfrs-font-bold bfrs-uppercase bfrs-tracking-[0.1em] bfrs-text-text-muted">{title}</div>
          {loading ? (
            <Skeleton variant="text" className="bfrs-mt-3 bfrs-h-6 bfrs-w-24" />
          ) : error ? (
            <div className="bfrs-mt-3 bfrs-text-sm bfrs-font-semibold bfrs-text-danger">{error}</div>
          ) : (
            <div className={cn("bfrs-mt-3 bfrs-truncate bfrs-font-extrabold bfrs-leading-none bfrs-text-text-primary", compact ? "bfrs-text-xl" : "bfrs-text-2xl")}>{value}</div>
          )}
        </div>
        {icon && <div className="bfrs-flex bfrs-size-9 bfrs-shrink-0 bfrs-items-center bfrs-justify-center bfrs-rounded-lg bfrs-bg-primary-soft bfrs-text-primary">{icon}</div>}
      </div>
      {(subtitle || description || trendValue || action) && (
        <div className="bfrs-mt-3 bfrs-flex bfrs-items-end bfrs-justify-between bfrs-gap-3">
          <div className="bfrs-min-w-0">
            {subtitle && <div className="bfrs-truncate bfrs-text-xs bfrs-font-medium bfrs-text-text-secondary">{subtitle}</div>}
            {description && <div className="bfrs-mt-1 bfrs-text-xs bfrs-leading-5 bfrs-text-text-muted">{description}</div>}
          </div>
          {trendValue && (
            <span className={cn("bfrs-inline-flex bfrs-shrink-0 bfrs-items-center bfrs-gap-1 bfrs-rounded-sm bfrs-px-2 bfrs-py-1 bfrs-text-[11px] bfrs-font-bold", trendClassName[trendDirection])}>
              {trendIcon[trendDirection]}
              {trendValue}
            </span>
          )}
          {action}
        </div>
      )}
    </Card>
  )
);

MetricCard.displayName = "MetricCard";
